// Attended read-only DB access for a Claude Code session: the approver's side.
// Mirrors POST /diagnostics-access/{lookup,approve,list,revoke}
// (.../api/diagnostics_access/); each is refused for anyone but an approver.
//
// See docs/Y2026-Q4/design-attended-db-diagnostics.md.
import { useMutation, useQuery, useQueryClient } from "react-query";

import type { PostJsonError } from "../http/postJson";
import { QK } from "../queryKeys";
import { useAgentsHttp, type AgentsHttp } from "../useAgentsHttp";

export type DiagnosticsGrantState =
  | "pending"
  | "lapsed"
  | "approved"
  | "active"
  | "expired"
  | "revoked";

export interface DiagnosticsGrant {
  grant_id: string;
  user_code: string;
  purpose: string;
  requester_ip: string | null;
  state: DiagnosticsGrantState;
  requested_at: string;
  request_expires_at: string;
  approved_at: string | null;
  approved_by_email: string | null;
  expires_at: string | null;
  token_issued_at: string | null;
  revoked_at: string | null;
  revoked_by_email: string | null;
}

// The server's own message (a lapsed code, not an approver) rather than the status line.
const serverMessage = (err: PostJsonError): Error => {
  try {
    const body = JSON.parse(err.bodyText ?? "") as { message?: string };
    if (body.message) return new Error(body.message);
  } catch {
    // Not JSON: the status line says what happened.
  }
  return err;
};

const post = <T,>(http: AgentsHttp, path: string, body: object) =>
  http<T>(path, body).catch((err: PostJsonError) => {
    throw serverMessage(err);
  });

export const useDiagnosticsGrants = () => {
  const http = useAgentsHttp();
  return useQuery<{ grants: DiagnosticsGrant[] }, Error>([QK.diagnosticsGrants], () =>
    post<{ grants: DiagnosticsGrant[] }>(http, "/diagnostics-access/list", {}),
  );
};

export const useLookupDiagnosticsRequest = () => {
  const http = useAgentsHttp();
  return useMutation<{ request: DiagnosticsGrant }, Error, string>((userCode) =>
    post<{ request: DiagnosticsGrant }>(http, "/diagnostics-access/lookup", { user_code: userCode }),
  );
};

export const useApproveDiagnosticsRequest = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<{ grant: DiagnosticsGrant }, Error, string>(
    (userCode) => post<{ grant: DiagnosticsGrant }>(http, "/diagnostics-access/approve", { user_code: userCode }),
    { onSuccess: () => void qc.invalidateQueries([QK.diagnosticsGrants]) },
  );
};

export const useRevokeDiagnosticsGrant = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<{ grant: DiagnosticsGrant }, Error, string>(
    (grantId) => post<{ grant: DiagnosticsGrant }>(http, "/diagnostics-access/revoke", { grant_id: grantId }),
    { onSuccess: () => void qc.invalidateQueries([QK.diagnosticsGrants]) },
  );
};
